import { useCart } from '../hooks/useCart';
import { formatCurrency } from '../helpers/formatters';
import { useLang } from '../hooks/useLanguage';
import IconToggle from '../icons/IconToggle';
import { useState } from 'react';
import { calculateOnNet } from '../helpers/calculateShowFees';
import { calculateProcessingFee } from '../helpers/calculateProcessingFee';
import Tooltip from '../components/Tooltip';
import { ShowPriceListingMode } from '../../openapi/model/showPriceListingMode';

// Fee display modes:
// 0 = no breakdown (final prices only)
// 1 = simple breakdown (taxes & fees combined)
// 2 = detailed breakdown (itemized)
//
// When priceListingMode === 'net', the saved isBreakingOutFees is overridden
// to mode 1 for display: net listings only make sense alongside an explicit
// taxes-and-fees row, and itemising on top of net listings is not part of
// the product spec.
function BookingSummaryTable({
    isBreakingOutFees,
    priceListingMode = ShowPriceListingMode.gross,
    showFees,
    pickedPaymentMethod,
    paymentMethods,
    absorbProcessingFee,
    orderAmount,
    cartOverride,
    discountOverride,
}: {
    isBreakingOutFees: number;
    priceListingMode?: ShowPriceListingMode;
    showFees: any[];
    pickedPaymentMethod: any;
    // The methods offered for this booking. A net-rate booking is only ever
    // offered its own net-rate method, so this tells us the booking is net-priced
    // BEFORE the guest picks anything.
    paymentMethods?: any[];
    absorbProcessingFee?: boolean;
    orderAmount?: number;
    // Reservation-details page can pass the pending order's items here so
    // the breakdown reflects what's being paid *now*, not whatever's in the
    // stale global cart store.
    cartOverride?: Array<{
        ticketTypeName: string;
        ticketTypeNameI18n?: any;
        ticketOptionName: string;
        ticketOptionNameI18n?: any;
        price: number;
        quantity: number;
    }>;
    // Pending-order discount applied server-side. `amount` is the rolled-up
    // inline discount (user coupon + verification-script adjustment); it can
    // be negative when the script's surcharge outweighs any coupon. `code` is
    // only set when the widget user entered the coupon themselves (carried
    // through from client cart state); admin-applied coupons leave `code`
    // null so the code stays hidden from the guest.
    discountOverride?: { amount: number; code: string | null };
}) {
    const cartCtx = useCart();
    const effectiveIsBreakingOutFees =
        priceListingMode === ShowPriceListingMode.net ? 1 : isBreakingOutFees;
    const useOverride = Array.isArray(cartOverride) && cartOverride.length > 0;
    const cart = useOverride ? cartOverride : cartCtx.cart;
    const totalPrice = useOverride
        ? cartOverride!.reduce((sum, i) => sum + i.price * (i.quantity || 1), 0)
        : cartCtx.totalPrice;
    const getFinalPriceWithDiscount = useOverride
        ? (fees: any[]) => {
              let gross = 0;
              for (const item of cartOverride!) {
                  const { finalPrice } = calculateOnNet(item.price, fees, true);
                  gross += finalPrice * (item.quantity || 1);
              }
              return gross;
          }
        : cartCtx.getFinalPriceWithDiscount;
    const getTaxesAndFeesAggregated = useOverride
        ? (fees: any[]) => {
              let totalFeeAmount = 0;
              let totalTaxAmount = 0;
              for (const item of cartOverride!) {
                  const { feeAmount, taxAmount } = calculateOnNet(item.price, fees, true);
                  totalFeeAmount += feeAmount * (item.quantity || 1);
                  totalTaxAmount += taxAmount * (item.quantity || 1);
              }
              return { totalFeeAmount, totalTaxAmount };
          }
        : cartCtx.getTaxesAndFeesAggregated;
    const appliedCoupon = useOverride
        ? discountOverride && discountOverride.amount !== 0
            ? {
                  // Synthetic coupon for the override case. Value comes from
                  // the server's rolled-up discount (user coupon + any
                  // verification-script adjustment). `code` is only populated
                  // when the widget user entered the code themselves —
                  // admin-applied coupons leave it null so the internal code
                  // stays hidden from the guest.
                  code: discountOverride.code,
                  discountType: 'fixed' as const,
                  discountValue: discountOverride.amount,
                  minOrderAmount: 0,
              }
            : null
        : cartCtx.appliedCoupon;
    const { getI18n, t } = useLang();
    const [summaryToggle, setSummaryToggle] = useState(true);

    // Net rate ignores taxes and the booking fee (they are already baked into the
    // retail rate the affiliate collected); the guest pays exactly the net. Zero
    // the aggregate so no fee row is shown and the totals stay net.
    //
    // Keyed on the BOOKING, not on a selection: the summary renders before the
    // guest picks a method, and a net booking is only ever offered its own
    // net-rate method (guest-card `net_rate` or company-card `net_rate_company`).
    const isNetRateMethod = (pm: any) =>
        pm?.methodType === 'net_rate' || pm?.methodType === 'net_rate_company';
    const isNetRate =
        (paymentMethods ?? []).some(isNetRateMethod) || isNetRateMethod(pickedPaymentMethod);

    const { totalFeeAmount: calculatedFees, totalTaxAmount: calculatedTaxes } =
        getTaxesAndFeesAggregated(showFees);
    const totalTaxesAndFees = isNetRate ? 0 : calculatedFees + calculatedTaxes;

    // Net inline discount = user coupon discount + verification-script
    // adjustment (script discount adds, script surcharge subtracts).
    // Override mode already arrives rolled up from the server.
    const getDiscountAmount = (): number => {
        if (useOverride) {
            return appliedCoupon ? appliedCoupon.discountValue : 0;
        }
        let couponDiscount = 0;
        if (appliedCoupon) {
            const priceWithFees = totalPrice + totalTaxesAndFees;
            if (!appliedCoupon.minOrderAmount || priceWithFees >= appliedCoupon.minOrderAmount) {
                if (appliedCoupon.discountType === 'percentage') {
                    couponDiscount = Math.floor(
                        (priceWithFees * appliedCoupon.discountValue) / 100,
                    );
                } else {
                    couponDiscount = Math.min(appliedCoupon.discountValue, priceWithFees);
                }
            }
        }
        return couponDiscount - cartCtx.verificationDeltaCents;
    };
    // A net discount can never exceed the gross it applies to. When combined
    // coupon + script discounts over-cover the order the total floors at 0
    // (baseFinalPrice below), so cap the displayed discount at the gross too -
    // otherwise the summary lines (subtotal + taxes - discount) would not sum
    // to the shown total. Surcharges (negative) are left untouched.
    const grossBeforeDiscount = totalPrice + totalTaxesAndFees;
    const rawDiscountAmount = getDiscountAmount();
    const discountAmount =
        rawDiscountAmount > 0
            ? Math.min(rawDiscountAmount, grossBeforeDiscount)
            : rawDiscountAmount;

    const baseFinalPrice =
        orderAmount !== undefined
            ? orderAmount
            : Math.max(0, getFinalPriceWithDiscount(showFees) + cartCtx.verificationDeltaCents);

    // Absorption only applies to Stripe card payments (mirrors the backend);
    // other method types keep their configured fee even on absorbing shows.
    const absorbsStripeFee = absorbProcessingFee && pickedPaymentMethod?.methodType === 'stripe';
    // Net rate charges the guest exactly the net price with no surcharge (mirrors
    // the backend, which zeroes the processing fee for net_rate payments).
    const processingFee =
        pickedPaymentMethod && !absorbsStripeFee && !isNetRate
            ? calculateProcessingFee(baseFinalPrice, {
                  percentage: Number(pickedPaymentMethod.percentage) || 0,
                  usd: Number(pickedPaymentMethod.usd) || 0,
              })
            : 0;

    const totalWithProcessingFee = baseFinalPrice + processingFee;

    return (
        <div className="fireknife-booking__summary">
            <div className="fireknife-booking__summary-table-box">
                <div
                    className="fireknife-booking__summary-total"
                    onClick={() => setSummaryToggle((prev) => !prev)}
                >
                    <div
                        className="fireknife-booking__summary-total-label fireknife-text-start"
                        aria-label={t('widgetBookingSummary.bookingTotal')}
                    >
                        {t('widgetBookingSummary.bookingTotal')}
                    </div>
                    <div className="fireknife-booking__summary-total-desc fireknife-text-end">
                        {formatCurrency(totalWithProcessingFee / 100, t('widgetLocale'))}
                        <button
                            className={`fireknife-booking__summary-btn-toggle  ${summaryToggle ? 'fireknife-booking__summary-btn-toggle--is-collapsed' : ''} fireknife-btn`}
                            type="button"
                        >
                            <IconToggle className="fireknife-booking__summary-btn-toggle-icon" />
                        </button>
                    </div>
                </div>

                <table className="fireknife-booking__summary-table">
                    <thead>
                        <tr>
                            <th className="fireknife-text-start">
                                {t('widgetBookingSummary.tableColumns.quantity')}
                            </th>
                            <th className="fireknife-text-start">
                                {t('widgetBookingSummary.tableColumns.name')}
                            </th>
                            <th className="fireknife-text-start">
                                {t('widgetBookingSummary.tableColumns.pricePerItem')}
                            </th>
                            <th className="fireknife-text-end">
                                {t('widgetBookingSummary.tableColumns.totalCost')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {cart.map((ticket, index) => {
                            const itemPrice = ticket.price; // net, in cents
                            const { finalPrice } = calculateOnNet(itemPrice, showFees, true);

                            // Mode 0 shows an all-in per-ticket price (gross).
                            // We render that EXCLUDING the processing fee so
                            // it lines up with its own row below — same
                            // ordering as the confirmation email.
                            const itemTotal = finalPrice / 100;
                            const lineTotal = (itemPrice * ticket.quantity) / 100;
                            const lineTotalWithFees = (finalPrice * ticket.quantity) / 100;

                            return (
                                <tr key={index}>
                                    <td
                                        className="fireknife-text-start"
                                        aria-label={t('widgetBookingSummary.tableColumns.quantity')}
                                    >
                                        {ticket.quantity}
                                    </td>
                                    <td
                                        className="fireknife-text-start"
                                        aria-label={t('widgetBookingSummary.tableColumns.name')}
                                        data-fireknife-quantity={ticket.quantity}
                                    >
                                        {getI18n(ticket.ticketTypeName, ticket.ticketTypeNameI18n)}{' '}
                                        {getI18n(
                                            ticket.ticketOptionName,
                                            ticket.ticketOptionNameI18n,
                                        )}
                                    </td>
                                    <td
                                        className="fireknife-text-start"
                                        aria-label={t(
                                            'widgetBookingSummary.tableColumns.pricePerItem',
                                        )}
                                        data-fireknife-unit={`pc`}
                                    >
                                        {effectiveIsBreakingOutFees === 0
                                            ? formatCurrency(itemTotal, t('widgetLocale'))
                                            : formatCurrency(ticket.price / 100, t('widgetLocale'))}
                                    </td>
                                    <td
                                        className="fireknife-text-end"
                                        aria-label={t(
                                            'widgetBookingSummary.tableColumns.totalCost',
                                        )}
                                    >
                                        {effectiveIsBreakingOutFees === 0
                                            ? formatCurrency(lineTotalWithFees, t('widgetLocale'))
                                            : formatCurrency(lineTotal, t('widgetLocale'))}
                                    </td>
                                </tr>
                            );
                        })}
                        {effectiveIsBreakingOutFees === 0 && (
                            <>
                                {discountAmount !== 0 && (
                                    <tr className="fireknife-booking__summary-discount">
                                        <th
                                            className="fireknife-text-start"
                                            aria-label={t('coupons.discount')}
                                            colSpan={3}
                                        >
                                            {t('coupons.discount')}
                                            {appliedCoupon?.code ? ` (${appliedCoupon.code})` : ''}
                                        </th>
                                        <td
                                            className="fireknife-text-end"
                                            style={{
                                                color: discountAmount > 0 ? 'green' : undefined,
                                            }}
                                        >
                                            {discountAmount > 0 ? '-' : '+'}
                                            {formatCurrency(
                                                Math.abs(discountAmount) / 100,
                                                t('widgetLocale'),
                                            )}
                                        </td>
                                    </tr>
                                )}
                                {processingFee > 0 && (
                                    <tr className="fireknife-booking__summary-taxes-and-fees">
                                        <th
                                            className="fireknife-text-start"
                                            aria-label={t(
                                                'widgetReservationInfo.paymentDetails.processingFee',
                                            )}
                                            colSpan={3}
                                        >
                                            {t(
                                                'widgetReservationInfo.paymentDetails.processingFee',
                                            )}
                                        </th>
                                        <td className="fireknife-text-end">
                                            {formatCurrency(processingFee / 100, t('widgetLocale'))}
                                        </td>
                                    </tr>
                                )}
                                <tr className="fireknife-booking__summary-subtotal">
                                    <th
                                        className="fireknife-text-start"
                                        aria-label={t('widgetBookingSummary.total')}
                                        colSpan={3}
                                    >
                                        {t('widgetBookingSummary.total') || 'Total'}
                                    </th>
                                    <td className="fireknife-text-end">
                                        <strong>
                                            {formatCurrency(
                                                totalWithProcessingFee / 100,
                                                t('widgetLocale'),
                                            )}
                                        </strong>
                                    </td>
                                </tr>
                            </>
                        )}

                        {/* Mode 1: Simple breakdown - subtotal + taxes & fees combined */}
                        {effectiveIsBreakingOutFees === 1 && (
                            <>
                                <tr className="fireknife-booking__summary-subtotal">
                                    <th
                                        className="fireknife-text-start"
                                        aria-label={t('widgetBookingSummary.subtotalNet')}
                                        colSpan={3}
                                    >
                                        {t('widgetBookingSummary.subtotalNet') ||
                                            t('widgetBookingSummary.subtotal') + ' (Net)'}
                                    </th>
                                    <td className="fireknife-text-end">
                                        {formatCurrency(totalPrice / 100, t('widgetLocale'))}
                                    </td>
                                </tr>
                                {totalTaxesAndFees > 0 && (
                                    <tr className="fireknife-booking__summary-taxes-and-fees">
                                        <th
                                            className="fireknife-text-start"
                                            aria-label={t('widgetBookingSummary.bookingFee')}
                                            colSpan={3}
                                        >
                                            <div className="fireknife-booking__summary-fee-label">
                                                + {t('widgetBookingSummary.bookingFee')}
                                                <Tooltip
                                                    ariaLabel={t(
                                                        'widgetBookingSummary.bookingFeeInfo',
                                                    )}
                                                    className="fireknife-booking-fee-tooltip"
                                                >
                                                    {t('widgetBookingSummary.bookingFeeInfoText')}
                                                </Tooltip>
                                            </div>
                                        </th>
                                        <td className="fireknife-text-end">
                                            {formatCurrency(
                                                totalTaxesAndFees / 100,
                                                t('widgetLocale'),
                                            )}
                                        </td>
                                    </tr>
                                )}
                                {discountAmount !== 0 && (
                                    <tr className="fireknife-booking__summary-discount">
                                        <th
                                            className="fireknife-text-start"
                                            aria-label={t('coupons.discount')}
                                            colSpan={3}
                                        >
                                            {discountAmount > 0 ? '- ' : '+ '}
                                            {t('coupons.discount')}
                                            {appliedCoupon?.code ? ` (${appliedCoupon.code})` : ''}
                                        </th>
                                        <td
                                            className="fireknife-text-end"
                                            style={{
                                                color: discountAmount > 0 ? 'green' : undefined,
                                            }}
                                        >
                                            {discountAmount > 0 ? '-' : '+'}
                                            {formatCurrency(
                                                Math.abs(discountAmount) / 100,
                                                t('widgetLocale'),
                                            )}
                                        </td>
                                    </tr>
                                )}
                                {processingFee > 0 && (
                                    <tr className="fireknife-booking__summary-taxes-and-fees">
                                        <th
                                            className="fireknife-text-start"
                                            aria-label={t(
                                                'widgetReservationInfo.paymentDetails.processingFee',
                                            )}
                                            colSpan={3}
                                        >
                                            {t(
                                                'widgetReservationInfo.paymentDetails.processingFee',
                                            )}
                                        </th>
                                        <td className="fireknife-text-end">
                                            {formatCurrency(processingFee / 100, t('widgetLocale'))}
                                        </td>
                                    </tr>
                                )}
                                <tr className="fireknife-booking__summary-subtotal">
                                    <th
                                        className="fireknife-text-start"
                                        aria-label={t('widgetBookingSummary.grandTotal')}
                                        colSpan={3}
                                    >
                                        = {t('widgetBookingSummary.grandTotal') || 'Grand Total'}
                                    </th>
                                    <td className="fireknife-text-end">
                                        <strong>
                                            {formatCurrency(
                                                totalWithProcessingFee / 100,
                                                t('widgetLocale'),
                                            )}
                                        </strong>
                                    </td>
                                </tr>
                            </>
                        )}

                        {/* Mode 2: Detailed breakdown - subtotal + each fee itemized */}
                        {effectiveIsBreakingOutFees === 2 && (
                            <>
                                <tr className="fireknife-booking__summary-subtotal">
                                    <th
                                        className="fireknife-text-start"
                                        aria-label={t('widgetBookingSummary.subtotalNet')}
                                        colSpan={3}
                                    >
                                        {t('widgetBookingSummary.subtotalNet') ||
                                            t('widgetBookingSummary.subtotal') + ' (Net)'}
                                    </th>
                                    <td className="fireknife-text-end">
                                        {formatCurrency(totalPrice / 100, t('widgetLocale'))}
                                    </td>
                                </tr>
                                {!isNetRate &&
                                    (() => {
                                        // Render non-tax fees first, then tax fees
                                        // (taxes are charged on net + fees, so showing
                                        // them after the fee lines matches the actual
                                        // order of operations and keeps the displayed
                                        // line items honest).
                                        //
                                        // Important: fees are applied per cart item, not
                                        // per booking — a flat $5 fee on 2 tickets is
                                        // $10 total. We accumulate per-fee totals by
                                        // walking each cart line, mirroring backend
                                        // calculateOnNet semantics.
                                        const nonTaxFees = showFees.filter((f: any) => !f.is_tax);
                                        const taxFees = showFees.filter((f: any) => f.is_tax);
                                        const ordered = [...nonTaxFees, ...taxFees];
                                        const perFeeTotals = new Map<any, number>();
                                        for (const item of cart) {
                                            let itemNonTaxSum = 0;
                                            for (const fee of nonTaxFees) {
                                                let amount = 0;
                                                if (fee.percentage > 0) {
                                                    amount += Math.ceil(
                                                        (item.price * fee.percentage) / 100,
                                                    );
                                                }
                                                if (fee.usd > 0) {
                                                    amount += Number(fee.usd) * 100;
                                                }
                                                amount *= item.quantity;
                                                itemNonTaxSum += amount;
                                                perFeeTotals.set(
                                                    fee,
                                                    (perFeeTotals.get(fee) || 0) + amount,
                                                );
                                            }
                                            const itemTaxBase =
                                                item.price + itemNonTaxSum / item.quantity;
                                            for (const fee of taxFees) {
                                                let amount = 0;
                                                if (fee.percentage > 0) {
                                                    amount += Math.ceil(
                                                        (itemTaxBase * fee.percentage) / 100,
                                                    );
                                                }
                                                if (fee.usd > 0) {
                                                    amount += Number(fee.usd) * 100;
                                                }
                                                amount *= item.quantity;
                                                perFeeTotals.set(
                                                    fee,
                                                    (perFeeTotals.get(fee) || 0) + amount,
                                                );
                                            }
                                        }
                                        return ordered.map((fee: any, idx: number) => (
                                            <tr
                                                className="fireknife-booking__summary-taxes-and-fees"
                                                key={idx}
                                            >
                                                <th
                                                    className="fireknife-text-start"
                                                    aria-label={fee.name}
                                                    colSpan={3}
                                                >
                                                    {getI18n(fee.name, fee.nameI18n)}
                                                </th>
                                                <td className="fireknife-text-end">
                                                    {formatCurrency(
                                                        (perFeeTotals.get(fee) || 0) / 100,
                                                        t('widgetLocale'),
                                                    )}
                                                </td>
                                            </tr>
                                        ));
                                    })()}
                                {discountAmount !== 0 && (
                                    <tr className="fireknife-booking__summary-discount">
                                        <th
                                            className="fireknife-text-start"
                                            aria-label={t('coupons.discount')}
                                            colSpan={3}
                                        >
                                            {discountAmount > 0 ? '- ' : '+ '}
                                            {t('coupons.discount')}
                                            {appliedCoupon?.code ? ` (${appliedCoupon.code})` : ''}
                                        </th>
                                        <td
                                            className="fireknife-text-end"
                                            style={{
                                                color: discountAmount > 0 ? 'green' : undefined,
                                            }}
                                        >
                                            {discountAmount > 0 ? '-' : '+'}
                                            {formatCurrency(
                                                Math.abs(discountAmount) / 100,
                                                t('widgetLocale'),
                                            )}
                                        </td>
                                    </tr>
                                )}
                                {processingFee > 0 && (
                                    <tr className="fireknife-booking__summary-taxes-and-fees">
                                        <th
                                            className="fireknife-text-start"
                                            aria-label={t(
                                                'widgetReservationInfo.paymentDetails.processingFee',
                                            )}
                                            colSpan={3}
                                        >
                                            {t(
                                                'widgetReservationInfo.paymentDetails.processingFee',
                                            )}
                                        </th>
                                        <td className="fireknife-text-end">
                                            {formatCurrency(processingFee / 100, t('widgetLocale'))}
                                        </td>
                                    </tr>
                                )}
                                <tr className="fireknife-booking__summary-subtotal">
                                    <th
                                        className="fireknife-text-start"
                                        aria-label={t('widgetBookingSummary.grandTotal')}
                                        colSpan={3}
                                    >
                                        = {t('widgetBookingSummary.grandTotal') || 'Grand Total'}
                                    </th>
                                    <td className="fireknife-text-end">
                                        <strong>
                                            {formatCurrency(
                                                totalWithProcessingFee / 100,
                                                t('widgetLocale'),
                                            )}
                                        </strong>
                                    </td>
                                </tr>
                            </>
                        )}
                    </tbody>
                </table>

                <div className="fireknife-booking__summary-desc fireknife-booking-desc">
                    {t('widgetFooter.notice')}
                </div>
            </div>
        </div>
    );
}

export default BookingSummaryTable;
